import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import type {
  LinkedTeamRegistration,
  RepPaymentSummary,
} from '@sportninja/common/features/registration/teamBudgetPayment/types'
import { shouldPollRepPayment } from '@sportninja/common/features/registration/teamBudgetPayment/utils/repPaymentPresentation'
import { fetchRegistrationSession } from '@sportninja/common/services/registration/teamBudgetPayment'

/** How often a started payment is re-checked, while it is. */
export const REP_PAYMENT_POLL_INTERVAL_MS = 3000

/** How long the step keeps checking before it offers to continue the payment. */
export const REP_PAYMENT_POLL_WINDOW_MS = 30000

interface UseRepPaymentStatusArgs {
  sessionId: string | null | undefined
  /** A Team Budget TEAM registration — the only kind a rep pays toward at sign-up. */
  enabled: boolean
}

export interface RepPaymentStatus {
  /** The invoice the rep chose to pay at registration, as the server holds it now. */
  repPayment: RepPaymentSummary | null
  /** The team registration as the server holds it now (its real approval status); null until fetched. */
  linkedTeamRegistration: LinkedTeamRegistration | null
  /** The fresh session has not arrived yet. */
  isLoading: boolean
  /** It could not be loaded. The wizard's own copy may predate the payment. */
  isUnavailable: boolean
  /** A started payment is still being re-checked (the first 30 seconds). */
  isChecking: boolean
  /** Ask the server again now; answers the fresh summary. */
  refresh: () => Promise<RepPaymentSummary | null>
}

/**
 * Sep 24 (B-7) — the confirmation step's view of the rep's payment toward the team
 * fee at registration.
 *
 * The session is refetched on EVERY entry, even when the wizard already holds one:
 * the wizard's context outlives the pay page, so a same-tab return still holds the
 * session as it was before the invoice existed. While a started payment has not
 * settled it is re-checked every 3 s for up to 30 s (the webhook usually lands in
 * seconds), and again whenever the rep comes back to the tab — a webhook that lands
 * later must still show. React Query stops the polling when the step unmounts.
 */
export const useRepPaymentStatus = ({
  sessionId,
  enabled,
}: UseRepPaymentStatusArgs): RepPaymentStatus => {
  'use memo'

  const isActive = enabled && Boolean(sessionId)
  const [isPollWindowOpen, setIsPollWindowOpen] = useState(true)

  useEffect(() => {
    if (!isActive) {
      return undefined
    }

    const timer = setTimeout(
      () => setIsPollWindowOpen(false),
      REP_PAYMENT_POLL_WINDOW_MS
    )

    return () => clearTimeout(timer)
  }, [isActive])

  const query = useQuery({
    queryKey: queryKeys.registrationSession.byId(sessionId),
    queryFn: () => fetchRegistrationSession(sessionId as string),
    enabled: isActive,
    // The app default keeps data fresh for five minutes; this must never be served
    // from cache.
    staleTime: 0,
    refetchOnMount: 'always',
    // The app default is off; a payment that settles after the poll window (or while the
    // tab was hidden) must still show when the rep comes back.
    refetchOnWindowFocus: true,
    refetchInterval: (current) =>
      isPollWindowOpen &&
      shouldPollRepPayment(
        current.state.data?.linked_team_registration?.rep_payment
      )
        ? REP_PAYMENT_POLL_INTERVAL_MS
        : false,
  })

  const repPayment = query.data?.linked_team_registration?.rep_payment ?? null

  return {
    repPayment,
    linkedTeamRegistration: query.isFetchedAfterMount
      ? query.data?.linked_team_registration ?? null
      : null,
    // Until THIS entry's fetch answers: a cached copy is exactly the stale answer
    // the refetch exists to replace.
    isLoading: isActive && !query.isFetchedAfterMount,
    // The latest fetch failed: whatever is held cannot be vouched for.
    isUnavailable: isActive && query.isError,
    isChecking: isPollWindowOpen && shouldPollRepPayment(repPayment),
    refresh: async () => {
      const result = await query.refetch()
      return result.data?.linked_team_registration?.rep_payment ?? null
    },
  }
}
